/**
 * Messaging domain event schemas.
 *
 * Inbound/outbound messenger agent events (Slack, Telegram, etc.).
 * Supabase `messaging_inbox` / `messaging_sessions` are source of truth.
 *
 * @module messaging.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

export const MessagingMessageReceivedEvent = BackboneEventSchema.extend({
  domain: z.literal('messaging'),
  type: z.literal('messaging.message.received'),
  payload: z.object({
    inboxId: z.string(),
    channel: z.enum(['slack', 'telegram', 'whatsapp', 'discord', 'messenger']),
    externalThreadId: z.string(),
    externalMessageId: z.string().optional(),
    senderExternalId: z.string().optional(),
    body: z.string(),
    userId: z.string().optional(),
    orgId: z.string().optional(),
    source: z.string(),
  }),
})

export const MessagingReplyReadyEvent = BackboneEventSchema.extend({
  domain: z.literal('messaging'),
  type: z.literal('messaging.reply.ready'),
  payload: z.object({
    sessionId: z.string().optional(),
    inboxId: z.string().optional(),
    channel: z.string(),
    externalThreadId: z.string(),
    userId: z.string(),
    body: z.string(),
    orgId: z.string().optional(),
    source: z.string(),
  }),
})

export const MessagingChannelLinkedEvent = BackboneEventSchema.extend({
  domain: z.literal('messaging'),
  type: z.literal('messaging.channel.linked'),
  payload: z.object({
    userId: z.string(),
    channel: z.string(),
    externalId: z.string(),
    connectionId: z.string().optional(),
    source: z.string(),
  }),
})

export const MessagingTypingEvent = BackboneEventSchema.extend({
  domain: z.literal('messaging'),
  type: z.literal('messaging.typing'),
  payload: z.object({
    channel: z.string(),
    externalThreadId: z.string(),
    userId: z.string(),
    source: z.string(),
  }),
})

export const MessagingDomainEvents = [
  MessagingMessageReceivedEvent,
  MessagingReplyReadyEvent,
  MessagingChannelLinkedEvent,
  MessagingTypingEvent,
] as const
